import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, nextTick, ref, watch } from "vue";



import { useLanguageStore } from "@/app/stores/language";
import { useArticleAttributesStore } from "@/data/stores/articleAttribute";
import { useTableAllocationStore } from "@/data/stores/tableAllocation";



import { Factory } from "@/data/dao/Factory";
import { KioskDao } from "@/data/dao/service/KioskDao";
import type { IKioskScreenSaverConfig } from "@/data/model/Kiosk";



import { logError } from "@/shared/logging/errors";



import { TastierClient } from "@/app/TastierClient";


export const useKioskStore = defineStore("kiosk", () => {
    const allocation = useTableAllocationStore();
    const articleAttributes = useArticleAttributesStore();
    const kioskDao = Factory.getInstance<KioskDao>(KioskDao);
    const language = useLanguageStore();

    const isKioskMode = ref(false);
    const isScreenSaverVisible = ref(false);
    const isFullWidth = ref(false);
    const isResettingSession = ref(false);
    const isKioskDebugMode = computed<boolean>(() => false);

    const screenSaverConfig = ref<IKioskScreenSaverConfig | undefined>(undefined);
    const isScreenSaverLoaded = computed(() => screenSaverConfig.value != undefined);
    const screenSaverImages = computed(() => screenSaverConfig.value?.images.map((image) => image.imagePath) ?? []);
    const screenSaverInterval = computed(() => screenSaverConfig.value?.transitionDelayMs ?? 5000);

    function toggleKioskFullscreen() {
        isFullWidth.value = !isFullWidth.value;
        if (isFullWidth.value) {
            document.documentElement.classList.add("kiosk-mode-normal-width");
        } else {
            document.documentElement.classList.remove("kiosk-mode-normal-width");
        }
    }

    async function loadScreenSaverConfig() {
        try {
            const config = await kioskDao.getScreenSaverConfig({ locationId: allocation.locationId });
            screenSaverConfig.value = config;
        } catch (ex) {
            logError(ex);
        }
    }

    // the language belongs to the guest standing at the terminal, so every reset that hands it
    // over resets it too. that is every reset raising the screen saver, and the start over of
    // the inactivity monitor, which goes back to the start page without one
    async function resetSession(showScreenSaver: boolean = true, resetLanguage: boolean = showScreenSaver) {
        try {
            // reset attribute store so the collection could be refreshed with the latest attributes
            articleAttributes.reset();
            isScreenSaverVisible.value = showScreenSaver;

            // Reset language - in the same tick as the screen saver, which then comes up in the
            // new language. done after the reset below instead, it lands a second later and the
            // customer watches the screen saver switch language in front of them
            if (resetLanguage) {
                language.resetLanguage();
            }

            await nextTick();
            setTimeout(() => {
                isResettingSession.value = true;
            }, 200);
            await TastierClient.identityContext.resetAnonymousSession();
        } catch (ex) {
            logError(ex);
        } finally {
            isResettingSession.value = false;
        }
    }

    function showScreenSaver() {
        isScreenSaverVisible.value = true;
    }

    function hideScreenSaver() {
        isScreenSaverVisible.value = false;
    }

    // on a terminal a long tap opens the browser context menu, which the guest can never close
    function preventContextMenu(e: Event) {
        e.preventDefault();
    }

    watch(isKioskMode, (newVal) => {
        if (newVal) {
            // add kiosk-mode class to html element
            document.documentElement.classList.add("kiosk-mode");
            document.addEventListener("contextmenu", preventContextMenu);

            const viteModeElement = document.querySelector(".vite-mode");
            if (viteModeElement) {
                viteModeElement.innerHTML = "Vite - Kiosk mode";
            }
        } else {
            document.documentElement.classList.remove("kiosk-mode");
            document.removeEventListener("contextmenu", preventContextMenu);
        }
    });

    watch(isResettingSession, (newVal) => {
        if (newVal) {
            document.documentElement.classList.add("kiosk-mode-resetting-session");
        } else {
            setTimeout(() => {
                document.documentElement.classList.remove("kiosk-mode-resetting-session");
            }, 500);
        }
    });

    watch(
        () => allocation.locationId,
        (newVal) => {
            if (newVal != undefined) {
                loadScreenSaverConfig();
            }
        }
    );

    return {
        // state
        isKioskMode,
        isScreenSaverVisible,
        isResettingSession,
        isFullWidth,
        isKioskDebugMode,

        // getters
        isScreenSaverLoaded,
        screenSaverImages,
        screenSaverInterval,

        // actions
        toggleKioskFullscreen,
        loadScreenSaverConfig,
        resetSession,
        showScreenSaver,
        hideScreenSaver,
    };
});

export type KioskStore = ReturnType<typeof useKioskStore>;

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useKioskStore, import.meta.hot));
}
